import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Contact, CreateContactInput } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { Modal } from '../../components/ui/Modal.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import {
  ContactFields,
  EMPTY_CONTACT,
  contactFormPayload,
  toContactFormState,
  type ContactFormState,
} from './contactForm.js';
import { useCreateContact, useDeleteContact, useUpdateContact } from './api.js';

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** `null` opens the dialog in create mode. */
  contact: Contact | null;
  canEdit: boolean;
  canDelete: boolean;
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

/**
 * Standalone contact editor used by the Contacts page. Contacts always belong to a
 * customer, so the customer picker is required on create (and locked afterwards —
 * reassigning a contact to a different customer is not supported).
 */
export function ContactFormModal({
  isOpen,
  onClose,
  contact,
  canEdit,
  canDelete,
}: ContactFormModalProps): JSX.Element {
  const isEdit = contact !== null;
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState('');
  const [form, setForm] = useState<ContactFormState>(EMPTY_CONTACT);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact();
  const deleteMutation = useDeleteContact();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;

  useEffect(() => {
    if (!isOpen) return;
    setForm(contact ? toContactFormState(contact) : EMPTY_CONTACT);
    setCustomerId(contact?.customerId ?? '');
    setCustomerError(null);
    setConfirmOpen(false);
    resetCreate();
    resetUpdate();
  }, [isOpen, contact, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError = mutation.isError
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : 'Failed to save contact.'
    : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const base = contactFormPayload(form);

    if (isEdit) {
      updateMutation.mutate({ id: contact.id, input: base }, { onSuccess: onClose });
      return;
    }

    if (!customerId) {
      setCustomerError('Select the customer this contact belongs to.');
      return;
    }
    const payload: CreateContactInput = { customerId, ...base };
    createMutation.mutate(payload, { onSuccess: onClose });
  };

  const handleDelete = (): void => {
    if (!isEdit) return;
    deleteMutation.mutate(contact.id, { onSuccess: onClose });
  };

  const goToCustomer = (): void => {
    const targetCustomerId = customerId || contact?.customerId;
    if (!targetCustomerId) return;
    navigate(`/customers?returnCustomerId=${targetCustomerId}&returnTab=contacts`);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit contact' : 'New contact'}
      size="lg"
      headerActions={
        <Button
          variant="secondary"
          size="sm"
          onClick={goToCustomer}
          disabled={!customerId && !contact?.customerId}
        >
          Go to Customer
        </Button>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {generalError ? (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {generalError}
          </div>
        ) : null}

        <CustomerSelectField
          label="Customer"
          required
          value={customerId}
          onChange={(id) => {
            setCustomerId(id);
            setCustomerError(null);
          }}
          disabled={isEdit}
          error={customerError ?? fieldErrors.customerId}
        />
        {isEdit ? (
          <p className="-mt-2 text-xs text-slate-500">
            A contact stays with the customer it was created under.
          </p>
        ) : null}

        <ContactFields form={form} setForm={setForm} fieldErrors={fieldErrors} contact={contact} />

        <div className="flex items-center gap-2 border-t border-slate-200 pt-3">
          {isEdit && canDelete ? (
            <Button
              type="button"
              variant="ghost"
              className="mr-auto text-rose-600 hover:bg-rose-50"
              onClick={() => setConfirmOpen(true)}
              disabled={mutation.isPending || deleteMutation.isPending}
            >
              Delete
            </Button>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
            className={isEdit && canDelete ? '' : 'ml-auto'}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!canEdit}>
            {isEdit ? 'Save contact' : 'Add contact'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete contact"
        message={`Delete "${`${form.firstName} ${form.lastName}`.trim()}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Modal>
  );
}
